import {useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {
    Card,
    Text,
    Group,
    Collapse,
    UnstyledButton,
    Loader,
} from "@mantine/core";
import {useDisclosure} from "@mantine/hooks";
import IconSearch from "../../../../assets/search.svg?react";
import IconChevronDown from "../../../../assets/chevron-down.svg?react";
import {Artifact, DirectSearchResult} from "../../../../types";
import {ResultRow} from "./ResultRow";
import {reorderResultsByMention} from "./mentionOrdering";
import {getArtifactTitle} from "./title";
import {truncateQuery} from "./queryDisplay";
import classes from "./SearchResultsRenderer.module.css";

interface SearchResultsRendererProps {
    artifact: Artifact;
    /**
     * Assistant prose for this message. When provided, results named in the prose (by markdown
     * link to their URL) float to the top of the card. Used by the tool-suggestion path.
     */
    messageContent?: string;
    collapsible?: boolean;
    defaultCollapsed?: boolean;
    title?: string;
    showResultsHint?: boolean;
    isLoading?: boolean;
    headerClassName?: string;
    headerGap?: number | string;
    onResultClick?: (result: DirectSearchResult, index: number) => void;
}

const INITIAL_VISIBLE = 3;

export function SearchResultsRenderer({
    artifact,
    messageContent,
    collapsible = false,
    defaultCollapsed = true,
    title,
    showResultsHint = false,
    isLoading = false,
    headerClassName,
    headerGap = "xs",
    onResultClick,
}: SearchResultsRendererProps) {
    const {t} = useTranslation();
    const initialOpen = collapsible ? !defaultCollapsed : true;
    const [opened, {toggle}] = useDisclosure(initialOpen);
    const [expanded, setExpanded] = useState(false);
    const payload = artifact.payload;
    const query = (payload?.query as string) ?? "";
    const results = useMemo(
        () => (payload?.results as DirectSearchResult[]) ?? [],
        [payload?.results]
    );
    const orderedResults = useMemo(
        () => reorderResultsByMention(results, messageContent),
        [results, messageContent]
    );

    if (results.length === 0 && !isLoading) {
        return (
            <Card withBorder p="md" radius={0}>
                <Text size="sm" c="dimmed">
                    {t("artifact.searchResults.noResults", {
                        query: truncateQuery(query),
                    })}
                </Text>
            </Card>
        );
    }

    const hiddenCount = orderedResults.length - INITIAL_VISIBLE;
    const displayedResults = expanded
        ? orderedResults
        : orderedResults.slice(0, INITIAL_VISIBLE);

    const headerTitle = title ?? getArtifactTitle(artifact);

    const isClosedCollapsible = collapsible && !opened;

    const headerClassNameComposed = [
        classes.header,
        collapsible ? classes.headerCollapsible : "",
        isClosedCollapsible ? classes.headerClosed : "",
        headerClassName ?? "",
    ]
        .filter(Boolean)
        .join(" ");

    const cardClassName = [
        classes.card,
        isClosedCollapsible ? classes.cardCollapsedNoBorder : "",
    ]
        .filter(Boolean)
        .join(" ");

    const HeaderTag = collapsible ? UnstyledButton : "div";
    const headerProps = collapsible
        ? {
              onClick: toggle,
              "aria-expanded": opened,
              w: "100%" as const,
          }
        : {};

    return (
        <Card withBorder radius={0} p={0} className={cardClassName}>
            <HeaderTag {...headerProps} className={headerClassNameComposed}>
                <Group gap={headerGap} wrap="nowrap" align="center">
                    <IconSearch
                        width={16}
                        height={16}
                        className={classes.headerIcon}
                    />
                    <Text size="sm" fw={600} c="dibblue.9">
                        {headerTitle}
                    </Text>
                    {showResultsHint && (
                        <span className={classes.resultsPill}>
                            {isLoading ? (
                                <span className={classes.resultsPillLoader}>
                                    <Loader
                                        type="dots"
                                        size="xs"
                                        color="gneis.5"
                                    />
                                </span>
                            ) : (
                                results.length
                            )}
                        </span>
                    )}
                    {collapsible && (
                        <Group
                            gap={4}
                            wrap="nowrap"
                            align="center"
                            className={classes.expandHint}
                        >
                            <Text size="sm" c="dibblue.7">
                                {opened
                                    ? t("artifact.searchResults.collapse", {
                                          defaultValue: "Skjul",
                                      })
                                    : t("artifact.searchResults.expand", {
                                          defaultValue: "Vis",
                                      })}
                            </Text>
                            <IconChevronDown
                                width={12}
                                height={12}
                                className={`${classes.expandHintIcon} ${opened ? classes.expandHintIconUp : ""}`}
                            />
                        </Group>
                    )}
                </Group>
            </HeaderTag>

            <Collapse in={opened}>
                {displayedResults.map((result, index) => (
                    <ResultRow
                        key={result.id}
                        result={result}
                        index={index}
                        showBorder={index > 0}
                        onClick={
                            onResultClick
                                ? () => onResultClick(result, index)
                                : undefined
                        }
                    />
                ))}

                {hiddenCount > 0 && (
                    <UnstyledButton
                        className={`${classes.expandButton} ${classes.resultRowBorder}`}
                        onClick={() => setExpanded((v) => !v)}
                    >
                        <Group gap={6}>
                            <IconChevronDown
                                width={12}
                                height={12}
                                className={`${classes.expandIcon} ${expanded ? classes.expandIconUp : ""}`}
                            />
                            <Text size="sm" c="dibblue.7">
                                {expanded
                                    ? t("artifact.searchResults.showFewer")
                                    : t("artifact.searchResults.showMore", {
                                          count: hiddenCount,
                                      })}
                            </Text>
                        </Group>
                    </UnstyledButton>
                )}
            </Collapse>
        </Card>
    );
}
